import { defineMessages, type MessageDescriptor } from 'react-intl';

import { LABS_FEATURE, type LabsFeatureId } from './labs-overrides';

/** Section-level and shared copy for the early-access list. */
export const earlyAccessMessages = defineMessages({
  sectionTitle: {
    defaultMessage: 'Experimental features',
    id: '5KLgwYVP1q',
    description: 'Heading for the per-feature early-access section on Heidi Labs',
  },
  zoneDescription: {
    defaultMessage:
      'Experimental features are early ideas we’re still shaping. Switch one on, try it for yourself and tell us how it goes. Your feedback helps us make it better for everyone.',
    id: '3Brub28rqZ',
    description: 'Intro paragraph above the Heidi Labs experiments list',
  },
  newBadgeLabel: {
    defaultMessage: 'New',
    id: '+VVhey/e4U',
    description: 'Badge marking a recently added Heidi Labs experiment',
  },
  enableAriaLabel: {
    defaultMessage: 'Enable {feature}',
    id: 'hC4rOkIEc/',
    description: 'Accessible label for an experiment’s opt-in switch',
  },
  enabledToast: {
    defaultMessage: '{feature} enabled',
    id: '1+bMKM2RXf',
    description: 'Toast confirming a Heidi Labs experiment was turned on',
  },
  disabledToast: {
    defaultMessage: '{feature} disabled',
    id: '8492J5kZ4x',
    description: 'Toast confirming a Heidi Labs experiment was turned off',
  },
  enabledToastBody: {
    defaultMessage: 'This experimental feature has been enabled.',
    id: 'cvMcBLIoVI',
    description: 'Toast body confirming a Heidi Labs experiment was turned on',
  },
  disabledToastBody: {
    defaultMessage: 'This experimental feature has been disabled.',
    id: 'AkrwAFEU/r',
    description: 'Toast body confirming a Heidi Labs experiment was turned off',
  },
  feedbackLink: {
    defaultMessage: 'Provide feedback',
    id: '7HKT3COESb',
    description:
      'Inline link on an experiment card that opens the experiment’s feedback board',
  },
  learnMoreLink: {
    defaultMessage: 'Learn more',
    id: 'HR7UwJAlqJ',
    description:
      'Footer link on an experiment card that opens the experiment’s help article',
  },
});

/** Per-experiment copy, keyed by feature id. */
const sessionLayoutV2 = defineMessages({
  title: {
    defaultMessage: 'Refreshed Scribe layout',
    id: 'vzp4FgJ7+k',
    description: 'Title of the Session Layout V2 experiment',
  },
  summary: {
    defaultMessage:
      'We’ve reorganised your controls into clear, logical groups.{br}A cleaner, calmer session surface that puts your documentation first. Easier to navigate, easier to focus.',
    id: 'GZbPaait2o',
    description: 'One-line summary of the Session Layout V2 experiment',
  },
  availability: {
    defaultMessage: 'Added August 2026',
    id: 'tCHqUVLWok',
    description: 'Static availability caption for the Session Layout V2 experiment',
  },
});

const adaptiveLearning = defineMessages({
  title: {
    defaultMessage: 'Adaptive learning',
    id: 'msyLUeZJt2',
    description: 'Title of the Adaptive learning experiment',
  },
  summary: {
    defaultMessage:
      'Heidi learns from your edits and saves them to memory, applying them to future notes automatically.{br}{br}This replaces any old personalizations, and generation may take slightly longer, but your notes will need fewer tweaks.',
    id: 'HZVlcmEAAZ',
    description: 'Summary of the Adaptive learning experiment',
  },
  availability: {
    defaultMessage: 'Added July 2026',
    id: 'ggilhvbIf5',
    description: 'Static availability caption for the Adaptive learning experiment',
  },
});

interface ExperimentCopy {
  title: MessageDescriptor;
  summary: MessageDescriptor;
  availability: MessageDescriptor;
}

// `satisfies Partial<…>` (not an exhaustive `Record<…>`): not every
// `LabsFeatureId` has a Labs settings card — `adaptive_learning`, for one, is
// offered via its own opt-in popover, not this registry. The literal type is
// preserved so known-key reads (registry.ts) stay non-optional.
export const experimentCopy = {
  [LABS_FEATURE.sessionLayoutV2]: sessionLayoutV2,
  [LABS_FEATURE.adaptiveLearning]: adaptiveLearning,
} satisfies Partial<Record<LabsFeatureId, ExperimentCopy>>;
